import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { CloseFullscreen, OpenInFull } from "@mui/icons-material";
import { SaveIcon, Settings, Undo2 } from "lucide-react";
import { useState } from "react";
import { t } from "i18next";
import DefectOptionPanel, { JsonOptionEditor } from "./DefectOptionPanel";
import { AugOptionsController } from "../../hooks/useAugOptions";
import { AUG_MODULE_NAMES, chainOrderWarnings, hiddenOptionKeys } from "../../utils/defectUtils";

// 이미지 풀스크린(Dialog 1400) 위 옵션 패널에서도 메뉴/JSON 풀스크린이 가려지지 않게
const JSON_DIALOG_Z = 1700;
const MENU_Z = 1800;

interface AugOptionSectionProps {
  ctrl: AugOptionsController;
  disabled?: boolean;
  maxHeight?: string;
}

export default function AugOptionSection({ ctrl, disabled = false, maxHeight = "90vh" }: AugOptionSectionProps) {
  const { editedAugOptions, initialOptions, optionsLoading, saving, jsonBase, jsonText, jsonError, jsonPretty } = ctrl;
  const [jsonFullscreen, setJsonFullscreen] = useState(false);
  const [settingAnchor, setSettingAnchor] = useState<HTMLElement | null>(null);
  const [newOptionAnchor, setNewOptionAnchor] = useState<HTMLElement | null>(null);

  const resetOptions = async () => {
    if (window.confirm("옵션을 초기화하시겠습니까?")) {
      setSettingAnchor(null);
      await ctrl.reloadOptions();
    }
  };

  const saveButton = (
    <Tooltip title={t("Save", "저장")}>
      <span>
        <IconButton size="small" onClick={ctrl.save} disabled={saving || optionsLoading || disabled}>
          {saving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" size="20px" />}
        </IconButton>
      </span>
    </Tooltip>
  );

  // JSON 편집기 + 새 옵션/비활성 옵션 버튼. 옵션 설정 칸과 JSON 풀스크린에서 같은 상태를 공유한다
  const renderJsonEditor = () => {
    const hidden = jsonError || !jsonBase ? [] : hiddenOptionKeys(jsonText, jsonBase);
    return (
      <>
        <JsonOptionEditor value={jsonText} onChange={ctrl.handleJsonChange} error={jsonError} />
        {!jsonError &&
          chainOrderWarnings(editedAugOptions).map((msg) => (
            <Typography key={msg} variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
              ⚠ {msg}
            </Typography>
          ))}
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {jsonError
              ? t("FixJsonFirstMsg", "JSON 형식을 고친 뒤 옵션을 추가할 수 있습니다")
              : t("AddOptionMsg", "옵션 추가 (비활성 옵션은 클릭하면 원래 값으로 다시 추가)")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            <Chip
              size="small"
              color="primary"
              label={`+ ${t("NewOption", "새 옵션")}`}
              disabled={!!jsonError}
              onClick={(e) => setNewOptionAnchor(e.currentTarget)}
            />
            {hidden.map((key) => (
              <Chip
                key={key}
                size="small"
                variant="outlined"
                label={`+ ${key}`}
                onClick={() => ctrl.restoreJsonOption(key)}
              />
            ))}
          </Box>
          <Menu
            anchorEl={newOptionAnchor}
            open={!!newOptionAnchor}
            onClose={() => setNewOptionAnchor(null)}
            sx={{ zIndex: MENU_Z }}
          >
            {AUG_MODULE_NAMES.map((name) => (
              <MenuItem
                key={name}
                dense
                onClick={() => {
                  setNewOptionAnchor(null);
                  ctrl.addNewOption(name);
                }}
              >
                {name}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </>
    );
  };

  return (
    <Box
      sx={{
        border: "1px solid var(--dk-border, #e0e0e0)",
        borderRadius: 2,
        p: 2,
        background: "var(--dk-surface-2, #fafafa)",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography fontWeight={600}>{t("OptionSetting")}</Typography>
          {/* 전체 옵션 on/off (저장은 Save/새로고침 때) */}
          <Tooltip title={ctrl.allOn ? t("DisableAllOptions", "전체 끄기") : t("EnableAllOptions", "전체 켜기")}>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  size="small"
                  checked={ctrl.allOn}
                  disabled={optionsLoading || !ctrl.canToggleAll || (!!jsonBase && !!jsonError)}
                  onChange={(e) => ctrl.setAllModes(e.target.checked)}
                />
              }
              label={<Typography variant="caption">{t("All", "전체")}</Typography>}
            />
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title={t("UndoOption", "뒤로 (옵션 on/off 되돌리기)")}>
            <span>
              <IconButton size="small" onClick={ctrl.undo} disabled={!ctrl.canUndo || optionsLoading || disabled}>
                <Undo2 size="20px" />
              </IconButton>
            </span>
          </Tooltip>
          {saveButton}
          <IconButton size="small" onClick={(e) => setSettingAnchor(e.currentTarget)}>
            <Settings fontSize="small" size="20px" />
          </IconButton>
          <Menu
            anchorEl={settingAnchor}
            open={!!settingAnchor}
            onClose={() => setSettingAnchor(null)}
            sx={{ zIndex: MENU_Z }}
          >
            {jsonBase && (
              <MenuItem disabled={!!jsonError} onClick={() => ctrl.handleJsonPretty(!jsonPretty)}>
                <FormControlLabel
                  control={<Switch size="small" checked={jsonPretty} />}
                  label="Prettier"
                  sx={{ m: 0, gap: 1, pointerEvents: "none" }}
                />
              </MenuItem>
            )}
            <MenuItem onClick={resetOptions}>{t("ResetOptions", "옵션 초기화 (현재 AI 옵션)")}</MenuItem>
          </Menu>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={jsonBase ? "json" : "ui"}
            onChange={(_, v) => v && v !== (jsonBase ? "json" : "ui") && ctrl.toggleJsonMode()}
            disabled={optionsLoading}
          >
            <ToggleButton value="ui">SIMPLE</ToggleButton>
            <ToggleButton value="json" disabled={!jsonBase && !Object.keys(editedAugOptions).length}>
              CODE
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ maxHeight, overflowY: "auto", pr: 0.5 }}>
        {optionsLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              py: 6,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t("OptionLoadingMsg", "옵션을 불러오는 중입니다...")}
            </Typography>
          </Box>
        ) : jsonBase ? (
          <>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {t(
                  "OptionJsonHelpMsg",
                  `옵션 추가 및 삭제가 가능합니다. 지운 옵션은 비활성화 됩니다. \n 줄 순서가 파이프라인 연결 순서가 됩니다.`,
                )}
              </Typography>
              <Tooltip title={t("JsonFullscreen", "JSON 편집기 크게 보기")}>
                <IconButton size="small" onClick={() => setJsonFullscreen(true)}>
                  <OpenInFull fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {renderJsonEditor()}
          </>
        ) : (
          <>
            {chainOrderWarnings(editedAugOptions).map((msg) => (
              <Typography key={msg} variant="caption" color="warning.main" sx={{ display: "block", mb: 0.5 }}>
                ⚠ {msg}
              </Typography>
            ))}
            <DefectOptionPanel
              options={editedAugOptions}
              initialOptions={initialOptions}
              onChange={ctrl.handleOptionChange}
              onReorder={ctrl.handleReorder}
              onSolo={ctrl.soloOption}
            />
          </>
        )}
      </Box>

      <Dialog
        fullScreen
        open={jsonFullscreen && !!jsonBase}
        onClose={() => setJsonFullscreen(false)}
        sx={{ zIndex: JSON_DIALOG_Z }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 3,
            py: 1.5,
            borderBottom: "1px solid var(--dk-border, #eee)",
          }}
        >
          <Typography fontWeight={700} sx={{ flex: 1 }}>
            {t("OptionSetting")} (JSON)
          </Typography>
          {saveButton}
          <IconButton onClick={() => setJsonFullscreen(false)} aria-label="close">
            <CloseFullscreen fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, p: 2, overflow: "auto" }}>
          {optionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            renderJsonEditor()
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
